/**
 * The one-time "maayot is now in <language>." notice (docs/plans/localization.md).
 *
 * Existing learners who never picked a language get the browser's language saved for them on
 * their next visit (profileLanguage.ts). This notice tells them once and offers English back.
 * Pure module apart from localStorage: no axios, no store, so tests and the shell import it cheaply.
 */
import {
  CORRECTIONS,
  DISCUSSION,
  HISTORY,
  INTRODUCTION,
  QUESTION,
  QUIZ,
  SETTING,
  STORIES,
  STORY,
  STORY_FINISHED,
  TASTER,
  WORDS,
} from '../routes/routeConstant'
import { LanguageCode, toNativeLanguageSource, toSupportedLanguage } from './languages'

/** Where this device remembers an answer until the server has it: `{ memberId, language }`. */
export const LANGUAGE_NOTICE_DECISION_KEY = 'maayot.langNoticeDecision'

/** Accounts younger than this are new sign-ups: they just went through a flow in their language. */
export const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000

// maayot's first accounts are from 2020; anything before 2015 is not a real creation date.
const EARLIEST_ACCOUNT_MS = Date.UTC(2015, 0, 1)
// A server clock slightly ahead of this device's must not make a fresh account look implausible.
const CLOCK_SKEW_MS = NEW_ACCOUNT_MS

/**
 * The learner pages of the signed-in app, where the notice may show. Everything else is left
 * alone: staff dashboards, live calls, auth, onboarding, checkout and claim flows, the landing
 * and marketing pages.
 */
const NOTICE_PATHS = [
  STORIES,
  INTRODUCTION,
  STORY,
  STORY_FINISHED,
  QUIZ,
  QUESTION,
  HISTORY,
  WORDS,
  CORRECTIONS,
  DISCUSSION,
  SETTING,
  TASTER,
]

export const isLanguageNoticePath = (pathname: string | null | undefined) =>
  typeof pathname === 'string' && NOTICE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))

/**
 * When a memberID is a Mongo ObjectId, its first 8 hex digits are the creation time in seconds.
 * MemberStack-era ids are; ids minted since the 2025-09-30 migration are random hex, so a value
 * before 2015 or in the future means "not a date" (null).
 */
export const memberIdCreatedAt = (memberId: unknown, now: number = Date.now()): number | null => {
  if (typeof memberId !== 'string' || !/^[0-9a-f]{24}$/i.test(memberId)) return null
  const ms = parseInt(memberId.slice(0, 8), 16) * 1000
  return ms >= EARLIEST_ACCOUNT_MS && ms <= now ? ms : null
}

const toTime = (value: unknown): number => {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim()) return Date.parse(value)
  return NaN
}

/**
 * When the account was created, or null when nothing says. The profile's `createdAt` is the
 * reliable source for accounts made since the migration (whose memberIDs are random); migrated
 * accounts all carry the 2025-09-30 migration date there, which still reads as old. The memberID
 * is the fallback when `createdAt` is missing.
 */
export const accountCreatedAt = (
  { createdAt, memberId }: { createdAt?: unknown; memberId?: unknown },
  now: number = Date.now(),
): number | null => {
  const fromProfile = toTime(createdAt)
  if (Number.isFinite(fromProfile) && fromProfile >= EARLIEST_ACCOUNT_MS && fromProfile <= now + CLOCK_SKEW_MS) {
    return fromProfile
  }
  return memberIdCreatedAt(memberId, now)
}

/** True only for an account known to be less than a day old. Unknown age counts as old. */
export const isNewAccount = (account: { createdAt?: unknown; memberId?: unknown }, now: number = Date.now()) => {
  const created = accountCreatedAt(account, now)
  return created !== null && now - created < NEW_ACCOUNT_MS
}

/** Whether this learner's language was set for them rather than picked (or is about to be). */
export const isDetectedLanguage = (nativeLanguage: unknown, nativeLanguageSource: unknown) => {
  const source = toNativeLanguageSource(nativeLanguageSource)
  if (source === 'detected') return true
  // No saved language yet: profileLanguage.ts is about to save the detected one.
  return !toSupportedLanguage(nativeLanguage) && source !== 'chosen'
}

export interface LanguageNoticeInput {
  memberId?: string | null
  createdAt?: unknown
  nativeLanguage?: unknown
  nativeLanguageSource?: unknown
  /** i18next's current language. */
  interfaceLanguage?: string | null
  pathname?: string | null
  /** This device already has an answer waiting to reach the server. */
  answered?: boolean
  /** A page opened in a given language from the translation review dashboard. */
  reviewOverride?: boolean
  now?: number
}

/**
 * Who sees the notice: a signed-in learner on a learner page, whose interface is not English,
 * whose language was detected (or is about to be), whose account is more than a day old, and who
 * has not answered it on this device.
 */
export const shouldShowLanguageNotice = ({
  memberId,
  createdAt,
  nativeLanguage,
  nativeLanguageSource,
  interfaceLanguage,
  pathname,
  answered = false,
  reviewOverride = false,
  now = Date.now(),
}: LanguageNoticeInput): boolean => {
  if (!memberId || answered || reviewOverride) return false
  if (!isLanguageNoticePath(pathname)) return false
  // The exact code only: the development pseudo-locale (en-XA) is English.
  const language = toSupportedLanguage(interfaceLanguage)
  if (!language || language === 'en' || interfaceLanguage !== language) return false
  if (!isDetectedLanguage(nativeLanguage, nativeLanguageSource)) return false
  return !isNewAccount({ createdAt, memberId }, now)
}

/** Each language as it reads inside its own sentence ("maayot est maintenant en français."). */
const NAME_IN_SENTENCE: Record<LanguageCode, string> = {
  en: 'English',
  fr: 'français',
  es: 'español',
  ja: '日本語',
  de: 'Deutsch',
  it: 'italiano',
  pt: 'português',
  ko: '한국어',
}

export const languageNameInSentence = (language: LanguageCode) => NAME_IN_SENTENCE[language]

const readJson = (key: string): any => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/** The answer this device is still holding for this learner, if any. */
export const readLanguageNoticeDecision = (memberId: string | null | undefined): LanguageCode | null => {
  if (!memberId) return null
  const decision = readJson(LANGUAGE_NOTICE_DECISION_KEY)
  if (!decision || decision.memberId !== memberId) return null
  return toSupportedLanguage(decision.language)
}

export const writeLanguageNoticeDecision = (memberId: string, language: LanguageCode) => {
  try {
    window.localStorage.setItem(LANGUAGE_NOTICE_DECISION_KEY, JSON.stringify({ memberId, language }))
  } catch {
    /* storage blocked: the store still hides the notice for this visit */
  }
}

// Answers given in this page load, saved or not: a profile response that left the server before
// the save landed must not bring the notice (or the detected language) back.
const answeredThisSession = new Map<string, LanguageCode>()

/** Records an answer: for this page load, and on this device until the server has it. */
export const rememberLanguageNoticeAnswer = (memberId: string, language: LanguageCode) => {
  answeredThisSession.set(memberId, language)
  writeLanguageNoticeDecision(memberId, language)
}

/** The learner's answer to the notice, if this page load or this device has one. */
export const languageNoticeAnswer = (memberId: string | null | undefined): LanguageCode | null => {
  if (!memberId) return null
  return readLanguageNoticeDecision(memberId) || answeredThisSession.get(memberId) || null
}

/** Test hook. */
export const resetLanguageNoticeSession = () => {
  answeredThisSession.clear()
}

export const clearLanguageNoticeDecision = () => {
  try {
    window.localStorage.removeItem(LANGUAGE_NOTICE_DECISION_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * The signed-in learner as the last visit left them in localStorage (`user`), so the notice can
 * be part of the first paint instead of pushing the header down when the profile arrives.
 */
export const readStoredCredentials = (): { memberId?: string; createdAt?: unknown; customFields?: any } | null => {
  const user = readJson('user')
  return user?.credentials?.memberId ? user.credentials : null
}

/**
 * Keeps the localStorage snapshot of the learner in step with a language save, so the next first
 * paint (readStoredCredentials) does not show the notice to someone who already answered it.
 */
export const patchStoredUserLanguage = (fields: object) => {
  try {
    const user = readJson('user')
    if (!user?.credentials) return
    user.credentials.customFields = { ...(user.credentials.customFields || {}), ...fields }
    window.localStorage.setItem('user', JSON.stringify(user))
  } catch {
    /* storage blocked: the profile load corrects it */
  }
}
